import React, { useState, useEffect } from "react";
import { bankService } from "../../services/bankService";
import { useAuth } from "../../contexts/AuthContext";
import {
  PlusIcon,
  TrashIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

const BankList = () => {
  const { user } = useAuth();
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [bankToDelete, setBankToDelete] = useState(null);
  const [message, setMessage] = useState("");
  const [formData, setFormData] = useState({
    bankName: "",
    bankCode: "",
    accountNumber: "",
  });
  const [resolvedAccountName, setResolvedAccountName] = useState("");

  useEffect(() => {
    fetchBanks();
  }, [user]);

  const fetchBanks = async () => {
    try {
      const data = await bankService.getUserBanks();
      setBanks(data);
    } catch (error) {
      console.error("Error fetching banks:", error);
      setMessage("Error loading bank accounts");
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setResolvedAccountName("");

    try {
      const response = await bankService.addBankAccount({
        bankName: formData.bankName,
        bankCode: formData.bankCode,
        accountNumber: formData.accountNumber,
      });
      const resolvedName = response?.data?.accountName || response?.accountName || "";
      setResolvedAccountName(resolvedName);
      setFormData({ bankName: "", bankCode: "", accountNumber: "" });
      setShowAddForm(false);
      await fetchBanks();
      setMessage("Bank account added successfully!");
    } catch (error) {
      console.error("Error adding bank account:", error);
      setMessage(error.response?.data?.message || "Error adding bank account");
    }
  };

  const handleDeleteClick = (bankId) => {
    setBankToDelete(bankId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bankToDelete) return;

    setMessage("");

    try {
      await bankService.deleteBankAccount(bankToDelete);
      await fetchBanks();
      setMessage("Bank account deleted successfully!");
      setShowDeleteConfirm(false);
      setBankToDelete(null);
    } catch (error) {
      console.error("Error deleting bank account:", error);
      setMessage(error.response?.data?.message || "Error deleting bank account");
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setBankToDelete(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Bank Accounts
            </h1>
            <p className="text-gray-600">
              Manage your bank accounts for receiving payments from material
              sales.
            </p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Bank Account
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-md ${
            message.includes("successfully")
              ? "bg-green-100 text-green-700 border border-green-200"
              : "bg-red-100 text-red-700 border border-red-200"
          }`}
        >
          <div className="flex items-center">
            {message.includes("successfully") ? (
              <CheckCircleIcon className="h-5 w-5 mr-2" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            )}
            {message}
          </div>
        </div>
      )}

      {/* Add Bank Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Add New Bank Account
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label
                  htmlFor="bankName"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Bank Name
                </label>
                <input
                  type="text"
                  id="bankName"
                  name="bankName"
                  required
                  value={formData.bankName}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="e.g., First Bank, GT Bank"
                />
              </div>
              <div>
                <label
                  htmlFor="bankCode"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Bank Code
                </label>
                <input
                  type="text"
                  id="bankCode"
                  name="bankCode"
                  required
                  value={formData.bankCode}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="e.g., 058 for GTBank"
                />
              </div>
              <div>
                <label
                  htmlFor="accountNumber"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Account Number
                </label>
                <input
                  type="text"
                  id="accountNumber"
                  name="accountNumber"
                  required
                  value={formData.accountNumber}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="10-digit account number"
                />
              </div>
            </div>
            {resolvedAccountName && (
              <div className="flex items-center text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">
                <CheckCircleIcon className="h-4 w-4 mr-2" />
                Resolved Account Name: <span className="font-semibold ml-1">{resolvedAccountName}</span>
              </div>
            )}
            <div className="flex space-x-3">
              <button
                type="submit"
                className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
              >
                Add Bank Account
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setFormData({
                    bankName: "",
                    bankCode: "",
                    accountNumber: "",
                  });
                  setResolvedAccountName("");
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Bank Accounts List */}
      {banks?.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <CreditCardIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No bank accounts
          </h2>
          <p className="text-gray-600 mb-6">
            Add a bank account to receive payments for your material sales.
          </p>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
          >
            Add Your First Bank Account
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Your Bank Accounts ({banks?.data?.bankDetails?.length})
            </h2>
          </div>

          <div className="divide-y divide-gray-200">
            {banks?.data?.bankDetails?.map((bank) => (
              <div key={bank._id} className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-green-100 rounded-lg">
                      <CreditCardIcon className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-medium text-gray-900">
                          {bank.bankName}
                        </h3>
                      </div>
                      <p className="text-sm text-gray-600">
                        Account: {bank.accountNumber}
                      </p>
                      <p className="text-sm text-gray-600">
                        Name: {bank.accountName}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleDeleteClick(bank._id)}
                      className="text-red-600 hover:text-red-800 p-2"
                      title="Delete bank account"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">
          Important Information
        </h3>
        <ul className="list-disc list-inside space-y-1 text-sm text-blue-800">
          <li>
            Bank accounts are used to receive payments for your material sales
          </li>
          <li>Ensure account details are accurate to avoid payment delays</li>
          <li>You can have multiple bank accounts for different purposes</li>
          <li>Account information is securely stored and encrypted</li>
        </ul>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-medium text-gray-900">
                Delete Bank Account
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this bank account? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankList;
